import { CommonFilterModel, CommonParams, CommonExportType } from '@/api/resources/common/shared/models/common.models'
import { ReportGridRequest } from '@/api/services/common.dto'
import { StatisticalAnalysisActivityStatisticsDossierBaseRequest, StatisticalAnalysisActivityStatisticsDossierCaseStates, StatisticalAnalysisActivityStatisticsDossierDetailExternalViewTable, StatisticalAnalysisActivityStatisticsDossierDetailRequest, StatisticalAnalysisActivityStatisticsDossierDetailViewChart, StatisticalAnalysisActivityStatisticsDossierDetailViewChartResponseInternalGroups, StatisticalAnalysisActivityStatisticsDossierDetailViewTable, StatisticalAnalysisActivityStatisticsDossierGroupRequest } from '@/modules/statistical-analysis-module/services/activity-dossier/activity-dossier.dto'
import { LaboratoryStatisticalAnalysisSidebarDateInput } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar-date-and-centers/statistical-analysis-sidebar-date-and-centers.model'
import { StatisticalAnalysisSearchBySidebarComboItemCombo } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar-search-by/statistical-analysis-sidebar-search-by.model'
import { StatisticalAnalysisViewDetailChartData } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart/statistical-analysis-view-detail-chart.model'
import { formatDatetimes, StatiscalAnalysisTypeCases, StatisticalAnalysisActivityTypes, StatisticalAnalysisDynamicObject } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisActivityDossierExternalInfo } from '@/modules/statistical-analysis-module/views/service-activity-dossier/service-activity-dossier.model'
import { Nullable } from '@/shared/types/app.types'
import axios, { AxiosResponse } from 'axios'
import { SeriesOptionsType } from 'highcharts'
import { chain, find, isNil, map } from 'lodash'
import { store } from '@/store/store'

class ActivityStatisticsDossierService {
    async getGeneralView(filters: StatisticalAnalysisActivityDossierExternalInfo): Promise<StatisticalAnalysisActivityStatisticsDossierCaseStates> {
        const baseRequest = this.getBaseRequest(filters)
        const typeCase = filters.typeSearch?.id?.toLocaleLowerCase()
        const params = baseRequest
        const response = await axios.get<StatisticalAnalysisActivityStatisticsDossierCaseStates>(`/statisticAnalysis/dossiers/activity/${typeCase}`, { params })
        return response.data
    }

    async getDetailViewTable(
        filterModel: CommonFilterModel, commonParams: CommonParams,
        entityTypes: StatiscalAnalysisTypeCases, baseRequest: StatisticalAnalysisActivityStatisticsDossierBaseRequest,
        detailRequest: StatisticalAnalysisActivityStatisticsDossierDetailRequest, scrollId: Nullable<string>): Promise<StatisticalAnalysisActivityStatisticsDossierDetailViewTable> {
        let extraParams = {
            ...filterModel,
            ...commonParams,
            ...baseRequest,
            ...detailRequest
        }
        if (scrollId) {
            extraParams = {
                ...extraParams,
                ...{ scrollId }
            }
        }
        const response = await axios.get<StatisticalAnalysisActivityStatisticsDossierDetailViewTable>(`/statisticAnalysis/dossiers/activity/${entityTypes?.toLocaleLowerCase()}/detail`, { params: extraParams })
        return response.data
    }

    async getDetailViewChart(entityType: StatiscalAnalysisTypeCases, baseRequest: StatisticalAnalysisActivityStatisticsDossierBaseRequest, groupRequest: StatisticalAnalysisActivityStatisticsDossierGroupRequest, yAxis: StatisticalAnalysisActivityTypes): Promise<StatisticalAnalysisViewDetailChartData> {
        const params = {
            ...baseRequest,
            ...groupRequest
        }
        const response = await axios.get<StatisticalAnalysisActivityStatisticsDossierDetailViewChart[]>(`/statisticAnalysis/dossiers/activity/${entityType?.toLocaleLowerCase()}/stats`, { params })
        return this._parseChartData(response.data, yAxis)
    }

    async getExternalViewDetailList(filterModel: CommonFilterModel, commonParams: CommonParams, baseRequest: StatisticalAnalysisActivityStatisticsDossierBaseRequest, groupingParams: StatisticalAnalysisDynamicObject, entityType: StatiscalAnalysisTypeCases, scrollId: Nullable<string>, totalElements: Nullable<number>): Promise<StatisticalAnalysisActivityStatisticsDossierDetailExternalViewTable> {
        const bodyParams = {
            ...groupingParams
        }
        let params = {
            ...filterModel,
            ...commonParams,
            ...baseRequest
        }

        if (!isNil(totalElements) && totalElements > 0) {
            params = {
                ...params,
                ...{ totalElements }
            }
        }

        if (scrollId) {
            params = {
                ...params,
                ...{ scrollId }
            }
        }
        const response = await axios.put<StatisticalAnalysisActivityStatisticsDossierDetailExternalViewTable>(`/statisticAnalysis/dossiers/activity/${entityType?.toLowerCase()}/detailList`, bodyParams, { params })
        return response.data
    }

    public getBaseRequest(filter: StatisticalAnalysisActivityDossierExternalInfo): StatisticalAnalysisActivityStatisticsDossierBaseRequest {
        return {
            startDate: encodeURIComponent(formatDatetimes(filter.startDate as Date)),
            endDate: encodeURIComponent(formatDatetimes(filter.endDate as Date)),
            laboratoryIds: filter.laboratories!.map((item: LaboratoryStatisticalAnalysisSidebarDateInput) => item.labId),
            stateIds: filter.states?.map((item: StatisticalAnalysisSearchBySidebarComboItemCombo) => item.id as string) as string[],
            laboratoryUserId: store.getters.labId
        }
    }

    public detailExportTo(
        externalFilter: StatisticalAnalysisActivityDossierExternalInfo,
        reportGridRequest: ReportGridRequest,
        groupByFields: string [],
        groupValueFilterRequests: StatisticalAnalysisDynamicObject[],
        exportType: CommonExportType
    ): Promise<AxiosResponse> {
        const params = {
            ...{ groupByFields },
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }

        const bodyParams = {
            gridRequest: {
                ...reportGridRequest
            },
            groupValueFilterRequests
        }

        return axios.put(`statisticAnalysis/dossiers/activity/case/detail/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then(response => response)
    }

    public detailExternalExportTo(
        externalFilter: StatisticalAnalysisActivityDossierExternalInfo,
        reportGridRequest: ReportGridRequest,
        groupValueFilterRequest: StatisticalAnalysisDynamicObject,
        exportType: CommonExportType
    ): Promise<AxiosResponse> {
        const params = {
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }

        const bodyParams = {
            gridRequest: {
                ...reportGridRequest
            },
            groupValueFilterRequest
        }

        return axios.put(`statisticAnalysis/dossiers/activity/case/detailList/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then(response => response)
    }

    private _parseChartData(response: StatisticalAnalysisActivityStatisticsDossierDetailViewChart[], yAxis: StatisticalAnalysisActivityTypes): StatisticalAnalysisViewDetailChartData {
        const categories = map(response, 'groupBy1')
        const labelSeries = chain(response).map('groupBy2').flatMap().map('name').uniq().value()
        const parsedResponse = this._getDataSeriesChart(labelSeries, response, yAxis)
        const series = chain(parsedResponse).map('groupBy2').flatMap().groupBy('name').mapValues((value: any, key: string) => {
            let keyColumn: string = ''
            switch (yAxis) {
            case StatisticalAnalysisActivityTypes.TOTAL:
                keyColumn = 'total'
                break
            case StatisticalAnalysisActivityTypes.URV:
                keyColumn = 'totalURV'
                break
            case StatisticalAnalysisActivityTypes.UCL:
                keyColumn = 'totalUCL'
                break
            default:
                keyColumn = 'total'
            }
            return {
                name: key,
                data: map(value, keyColumn)
            }
        }).flatMap().value() as SeriesOptionsType[]
        return {
            categories,
            series
        }
    }

    private _getDataSeriesChart(uniqLabels: string[], response:StatisticalAnalysisActivityStatisticsDossierDetailViewChart[], yAxis: StatisticalAnalysisActivityTypes): StatisticalAnalysisActivityStatisticsDossierDetailViewChart[] {
        return response.map((groupResponse: StatisticalAnalysisActivityStatisticsDossierDetailViewChart) => {
            groupResponse.groupBy2 = uniqLabels.map((uniqLabel: string) => {
                const foundGroupBy2Item = find(groupResponse.groupBy2, { 'name': uniqLabel })
                if (foundGroupBy2Item) {
                    return foundGroupBy2Item
                } else {
                    return {
                        name: uniqLabel,
                        total: null,
                        totalUCL: null,
                        totalURV: null
                    }
                }
            }) as StatisticalAnalysisActivityStatisticsDossierDetailViewChartResponseInternalGroups[]
            return groupResponse
        })
    }
}

export const activityStatisticsDossierService = new ActivityStatisticsDossierService()
